import { FC, useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '../hooks/useAudio';
import { Icon } from './icons/Icon';
import { resolveMediaUrl } from '../services/api';

const formatTime = (s: number): string => {
    if (!isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
};

export const GlobalPlayer: FC = () => {
    const { currentTrack, isPlaying, currentTime, duration, toggle, seek, stop } = useAudio();
    const progressRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!progressRef.current || !duration) return;
        const rect = progressRef.current.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        seek(pct * duration);
    }, [duration, seek]);

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        setIsDragging(true);
        handleProgressClick(e);
    }, [handleProgressClick]);

    useEffect(() => {
        if (!isDragging) return;
        const handleMouseMove = (e: MouseEvent) => {
            if (!progressRef.current || !duration) return;
            const rect = progressRef.current.getBoundingClientRect();
            const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            seek(pct * duration);
        };
        const handleMouseUp = () => setIsDragging(false);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, duration, seek]);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    // 本番は別オリジンなので絶対URL化する → CLAUDE.md §4
    const imgUrl = resolveMediaUrl(currentTrack?.imageUrl);

    return (
        <AnimatePresence>
            {currentTrack && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="fixed bottom-14 md:bottom-0 left-0 right-0 z-[60] bg-surface border-t-2 border-ink"
                >
                    <div
                        ref={progressRef}
                        className="h-1.5 w-full bg-cream-2 cursor-pointer group relative"
                        onMouseDown={handleMouseDown}
                    >
                        <div className="h-full bg-pop transition-[width] duration-100" style={{ width: `${progress}%` }} />
                        <div
                            className="absolute top-1/2 w-3.5 h-3.5 rounded-full bg-ink border-2 border-surface opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ left: `${progress}%`, transform: `translate(-50%, -50%)` }}
                        />
                    </div>

                    <div className="flex items-center gap-3 px-4 py-2.5 max-w-screen-xl mx-auto">
                        <div className="w-10 h-10 rounded-pop-sm overflow-hidden flex-shrink-0 bg-cream-2 border-2 border-ink flex items-center justify-center">
                            {imgUrl ? (
                                <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <Icon name="music" size={18} className="text-ink-3" />
                            )}
                        </div>

                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-ink truncate">{currentTrack.title || '再生中'}</p>
                            <p className="text-xs text-ink-2 truncate flex items-center gap-1">
                                <Icon name="voice" size={12} className="text-role-voice" /> {currentTrack.authorName}
                            </p>
                        </div>

                        <div className="text-xs text-ink-3 font-mono hidden sm:block">
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={toggle}
                                aria-label={isPlaying ? '一時停止' : '再生'}
                                className="w-10 h-10 rounded-full bg-ink text-sun border-2 border-ink flex items-center justify-center hover:-translate-y-0.5 active:translate-y-0 transition-transform"
                            >
                                <Icon name={isPlaying ? 'pause' : 'play'} size={16} />
                            </button>
                            <button
                                onClick={stop}
                                aria-label="停止"
                                className="w-8 h-8 rounded-full bg-cream-2 border-2 border-ink flex items-center justify-center text-ink hover:bg-sun transition-colors"
                            >
                                <Icon name="x" size={14} />
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
